// hooks/useTheme.ts — 3-state theme wired to the DOM (spec §4.5).
import { useCallback, useEffect, useState } from 'react';
import { applyTheme, loadTheme, nextTheme, saveTheme, type Theme } from '../lib/theme';

export interface UseTheme {
  theme: Theme;
  setTheme: (t: Theme) => void;
  cycle: () => void;
}

export function useTheme(): UseTheme {
  const [theme, setThemeState] = useState<Theme>(() =>
    typeof localStorage === 'undefined' ? 'system' : loadTheme(localStorage),
  );

  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => applyTheme(document.documentElement, theme, mql.matches);
    apply();
    mql.addEventListener('change', apply);
    return () => mql.removeEventListener('change', apply);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    saveTheme(localStorage, t);
    setThemeState(t);
  }, []);

  const cycle = useCallback(() => setTheme(nextTheme(theme)), [theme, setTheme]);

  return { theme, setTheme, cycle };
}
